import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  deleteSharedModelById,
  type DeleteSharedModelInput,
  getSharedModelById,
  type GetSharedModelByIdInput,
  getSharedModelOutput,
  type GetSharedModelOutputInput,
  getSharedModels,
  getSharedModelsByFilter,
  type GetSharedModelsByFilterInput,
  getSharedModelSharingInvitations,
  type GetSharedModelSharingInvitationsInput,
  type GetSharedModelsInput,
  importSharedModel,
  type ImportSharedModelInput,
  revokeSharedModel,
  type RevokeSharedModelInput,
  shareModel,
  type ShareModelInput,
  shareModelToHost,
  type ShareModelToHostInput,
} from '../../api/sharedModel';
import {
  type TypeModel,
  type TypeSharedModel,
  type TypeSharedModelSharingInvitation,
} from '../../common/dataTypes';
import { type DefaultMutationError, type DefaultQueryError } from '..';

export const useGetSharedModelOutput = (
  input: GetSharedModelOutputInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, Record<string, number[]>>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getSharedModelOutput', input],
    queryFn: async () => {
      const {
        data: model,
      }: AxiosResponse<Record<string, Record<string, number[]>>> =
        await getSharedModelOutput(input);

      return model;
    },
    ...opts,
  });

export const useShareModel = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeModel>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['shareModel'],
    mutationFn: async (input: ShareModelInput) => {
      const { data } = await shareModel(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getSharedModels'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getSharedModelsByFilter'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getSharedRequests'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getSentInvitations'],
      });

      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useShareModelToHost = (
  opts?: Partial<
    UseMutationOptions<Awaited<TypeModel>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['shareModelToHost'],
    mutationFn: async (input: ShareModelToHostInput) => {
      const { data } = await shareModelToHost(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: [
          'getSharedModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useImportSharedModel = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['importSharedModel'],
    mutationFn: async (input: ImportSharedModelInput) => {
      const { data }: AxiosResponse = await importSharedModel(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: [
          'getModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useGetSharedModelSharingInvitations = (
  input: GetSharedModelSharingInvitationsInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<TypeSharedModelSharingInvitation[]>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    queryKey: ['getSharedModelSharingInvitations', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModelSharingInvitation[]> =
        await getSharedModelSharingInvitations(input);

      return data;
    },
    ...opts,
  });

export const useGetSharedModels = (
  input: GetSharedModelsInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeSharedModel[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getSharedModels', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModel[]> =
        await getSharedModels(input);

      return data.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    },
    ...opts,
  });

export const useGetSharedModelsByFilter = (
  input: GetSharedModelsByFilterInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeSharedModel[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getSharedModelsByFilter', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModel[]> =
        await getSharedModelsByFilter(input);

      return data.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    },
    ...opts,
  });

export const useGetSharedModelById = (
  input: GetSharedModelByIdInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeSharedModel>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getSharedModelById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModel> =
        await getSharedModelById(input);

      return data;
    },
    ...opts,
  });

export const useRevokeSharedModel = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['revokeSharedModel'],
    mutationFn: async (input: RevokeSharedModelInput) => {
      const { data }: AxiosResponse = await revokeSharedModel(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: [
            'getSharedModels',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: [
            'getProjects',
            {
              orgId: variables.orgId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: [
            'getProjectById',
            {
              orgId: variables.orgId,
              projectId: variables.projectId,
            },
          ],
        }),
        queryClient.refetchQueries({
          queryKey: ['getSharedModelById', variables],
        }),
      ]);
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useDeleteSharedModel = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteSharedModel'],
    mutationFn: (input: DeleteSharedModelInput) => deleteSharedModelById(input),
    onSuccess(data, variables, unknown, context) {
      (queryClient.refetchQueries({
        queryKey: [
          'getSharedModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      }),
        opts?.onSuccess?.(data, variables, unknown, context));
    },
  });
};
